'use client';

import React, { useState, useRef, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Offer, Category } from '@/types';
import { Search, MapPin, Mountain, Home, Landmark, UtensilsCrossed, Dumbbell, Waves, Leaf, Tv, ChevronRight, Heart, Share2, Star, Sparkles, Crown, Tag, Loader2, TrendingUp, Navigation, X } from 'lucide-react';
import SafeImage from '@/components/SafeImage';
import { toast } from 'sonner';
import LegalFooter from '@/components/LegalFooter';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import InquiryModal from '@/app/offer-detail-page/components/InquiryModal';

const SEARCH_STORAGE_KEY = 'discovery_search_value';

interface FeedAd {
  id: string;
  title: string;
  image_url: string | null;
  target_url: string | null;
}

const FEED_AD_EVERY = 4; // show an ad every 4 offers

interface DiscoveryPageContentProps {
  offers: Offer[];
  categories: Category[];
  specialCount: number;
  epicCount: number;
}

const iconMap: Record<string, React.ReactNode> = {
  mountain: <Mountain size={15} strokeWidth={2} />,
  home: <Home size={15} strokeWidth={2} />,
  landmark: <Landmark size={15} strokeWidth={2} />,
  utensils: <UtensilsCrossed size={15} strokeWidth={2} />,
  dumbbell: <Dumbbell size={15} strokeWidth={2} />,
  spa: <Waves size={15} strokeWidth={2} />,
  leaf: <Leaf size={15} strokeWidth={2} />
};

function haversineKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a =
  Math.sin(dLat / 2) * Math.sin(dLat / 2) +
  Math.cos(lat1 * Math.PI / 180) *
  Math.cos(lat2 * Math.PI / 180) *
  Math.sin(dLon / 2) *
  Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export default function DiscoveryPageContent({
  offers,
  categories,
  specialCount,
  epicCount
}: DiscoveryPageContentProps) {
  const { user, favoriteIds, toggleFavorite: supabaseToggleFavorite } = useAuth();
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [locating, setLocating] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [displayOffers, setDisplayOffers] = useState<Offer[]>(offers);
  const [nearbyActive, setNearbyActive] = useState(false);
  const [inquiryOffer, setInquiryOffer] = useState<Offer | null>(null);
  const categoryScrollRef = useRef<HTMLDivElement>(null);
  const supabase = createClient();
  const [feedAds, setFeedAds] = useState<FeedAd[]>([]);

  // Load feed ads once
  React.useEffect(() => {
    supabase
      .from('ads')
      .select('id, title, image_url, target_url')
      .eq('is_active', true)
      .eq('placement', 'feed')
      .limit(10)
      .then(({ data }) => {
        if (data && data.length > 0) setFeedAds(data);
      });
  }, []);

  // Restore search from sessionStorage on mount
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const saved = sessionStorage.getItem(SEARCH_STORAGE_KEY);
      if (saved) setSearchValue(saved);
    }
  }, []);

  // Persist search to sessionStorage whenever it changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (searchValue) {
        sessionStorage.setItem(SEARCH_STORAGE_KEY, searchValue);
      } else {
        sessionStorage.removeItem(SEARCH_STORAGE_KEY);
      }
    }
  }, [searchValue]);

  const toggleFavorite = async (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error('Bitte melde dich an, um Favoriten zu speichern');
      return;
    }
    const added = await supabaseToggleFavorite(id);
    if (added) {
      toast.success('Zu Favoriten hinzugefügt', { icon: '❤️', duration: 1800 });
    }
  };

  const handleShare = (offer: Offer, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (navigator.share) {
      navigator.share({ title: offer.title, url: window.location.href });
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link kopiert!', { icon: '🔗', duration: 1800 });
    }
  };

  const handleNearby = () => {
    if (locating) return;
    setLocating(true);
    if (typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const userLat = pos.coords.latitude;
          const userLon = pos.coords.longitude;

          // Calculate distance for each offer that has coordinates, then sort
          const withDistance = offers.map((o) => {
            if (o.latitude != null && o.longitude != null) {
              return { ...o, distance_km: haversineKm(userLat, userLon, o.latitude, o.longitude) };
            }
            return o;
          });

          // Sort: offers with distance first (nearest first), then offers without coords
          const sorted = [...withDistance].sort((a, b) => {
            if (a.distance_km != null && b.distance_km != null) return a.distance_km - b.distance_km;
            if (a.distance_km != null) return -1;
            if (b.distance_km != null) return 1;
            return 0;
          });

          setDisplayOffers(sorted);
          setNearbyActive(true);
          setLocating(false);
          toast.success('Angebote nach Entfernung sortiert', {
            description: 'Nächste Angebote zuerst',
            icon: '📍'
          });
        },
        () => {
          setLocating(false);
          toast.error('Standortzugriff verweigert', {
            description: 'Bitte erlaube Standortzugriff in deinen Einstellungen.'
          });
        },
        { timeout: 8000 }
      );
    } else {
      setLocating(false);
      toast.error('Geolocation nicht verfügbar in diesem Browser.');
    }
  };

  const handleOfferClick = async (offerId: string) => {
    try {
      await supabase.rpc('increment_offer_click', { offer_uuid: offerId });
    } catch {
      // silently ignore — click tracking is non-critical
    }
  };

  const baseOffers = nearbyActive ? displayOffers : offers;

  const filteredOffers = baseOffers.filter((o) => {
    const matchesCategory =
    !activeCategory || o.categories.some((c) => c.id === activeCategory);
    const matchesSearch =
    !searchValue.trim() ||
    o.title.toLowerCase().includes(searchValue.toLowerCase()) ||
    o.city.toLowerCase().includes(searchValue.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="pb-2">
      {/* ── HEADER ── */}
      <div className="pt-5 pb-3 px-4 text-center" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 20px)' }}>
        {/* WINEWOOD Logo */}
        <div className="flex flex-col items-center mb-1">
          <div className="relative mb-1" style={{ width: 200, height: 60, overflow: 'hidden' }}>
            <Image
              src="/assets/images/logo_grun_fur_app-1787922846254.png"
              alt="WINEWOOD Adventure Logo"
              fill
              className="object-contain object-center"
              priority />
            
          </div>
        </div>

        {/* Taglines */}
        <p className="text-sm mt-1" style={{ color: 'var(--muted-foreground)' }}>
          Freizeitangebote in deiner Nähe
        </p>
        <p className="text-lg font-bold mt-0.5">
          <span className="text-card-foreground">Dein Standort.</span>{' '}
          <span style={{ color: 'var(--primary)' }}>Dein Abenteuer.</span>
        </p>
      </div>

      {/* ── SEARCH BAR ── */}
      <div className="px-4 mb-4">
        <div
          className="flex items-center gap-3 px-4 py-3.5 rounded-pill"
          style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
          
          <Search size={16} color="var(--muted-foreground)" strokeWidth={2} />
          <input
            type="search"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="Suche Aktivitäten, Orte ..."
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: searchValue ? 'var(--card-foreground)' : 'var(--muted-foreground)' }}
            aria-label="Aktivitäten und Orte suchen" />
          {searchValue ? (
            <button
              onClick={() => setSearchValue('')}
              className="flex items-center justify-center w-6 h-6 rounded-full flex-shrink-0 transition-all duration-150 active:scale-90"
              style={{ background: 'var(--primary)', color: '#fff' }}
              aria-label="Suche löschen">
              <X size={13} strokeWidth={2.5} />
            </button>
          ) : null}
        </div>
        {searchValue ? (
          <div className="flex items-center gap-2 mt-2 px-1">
            <MapPin size={13} color="var(--primary)" strokeWidth={2} />
            <span className="text-xs font-medium" style={{ color: 'var(--card-foreground)' }}>
              Aktive Suche: <span style={{ color: 'var(--primary)' }}>{searchValue}</span>
            </span>
            <button
              onClick={() => setSearchValue('')}
              className="ml-auto text-xs font-semibold px-2.5 py-1 rounded-pill transition-all duration-150 active:scale-95"
              style={{ background: 'rgba(124,179,66,0.15)', color: 'var(--primary)', border: '1px solid rgba(124,179,66,0.4)' }}
              aria-label="Suche zurücksetzen">
              ✕ Suche löschen
            </button>
          </div>
        ) : null}
      </div>

      {/* ── CATEGORY PILLS ── */}
      <div className="px-4 mb-4">
        <div
          ref={categoryScrollRef}
          className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
          
          {categories.map((cat) => {
            const isActive = activeCategory === cat.id;
            return (
              <button
                key={`disc-cat-${cat.id}`}
                onClick={() => setActiveCategory(isActive ? null : cat.id)}
                className="flex items-center gap-1.5 px-4 py-2 rounded-pill text-xs font-medium whitespace-nowrap transition-all duration-150 active:scale-95 flex-shrink-0"
                style={{
                  background: isActive ? 'var(--card-elevated)' : 'var(--muted)',
                  color: isActive ? 'var(--card-foreground)' : 'var(--secondary-foreground)',
                  border: isActive ?
                  '1px solid var(--border)' :
                  '1px solid var(--border)'
                }}
                aria-pressed={isActive}>
                
                <span style={{ color: isActive ? 'var(--primary)' : 'var(--muted-foreground)' }}>
                  {iconMap[cat.icon] ?? <Leaf size={15} />}
                </span>
                {cat.name}
              </button>);

          })}
        </div>
      </div>

      {/* ── HERO TILES (Spezial + Epic) ── */}
      <div className="px-4 mb-4 grid grid-cols-2 gap-3">
        {/* Spezial Angebote */}
        <Link href="/specials">
          <div
            className="relative rounded-app overflow-hidden transition-transform duration-150 active:scale-[0.97]"
            style={{ height: '120px', border: '1.5px solid rgba(124,179,66,0.45)', background: 'rgba(20,50,5,0.85)' }}>
            
            {/* Star icon top-left */}
            <div
              className="absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                background: 'rgba(124,179,66,0.22)',
                border: '1.5px solid rgba(124,179,66,0.6)'
              }}>
              
              <Star size={17} color="#7CB342" fill="#7CB342" />
            </div>

            {/* Count badge top-right */}
            <div
              className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
              style={{ background: 'var(--primary)' }}>
              
              {specialCount}
            </div>

            {/* Text bottom */}
            <div className="absolute bottom-3 left-3 right-3">
              <p className="text-sm font-bold text-white leading-tight">Spezial Angebote</p>
              <div className="flex items-center gap-0.5 mt-0.5">
                <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.75)' }}>
                  Alle ansehen
                </span>
                <ChevronRight size={11} color="rgba(255,255,255,0.75)" />
              </div>
            </div>
          </div>
        </Link>

        {/* Epic Locations */}
        <Link href="/epic">
          <div
            className="relative rounded-app overflow-hidden transition-transform duration-150 active:scale-[0.97]"
            style={{ height: '120px', border: '1.5px solid rgba(168,85,247,0.45)', background: 'rgba(40,10,70,0.9)' }}>
            
            {/* Sparkles icon top-left */}
            <div
              className="absolute top-3 left-3 w-9 h-9 rounded-full flex items-center justify-center"
              style={{
                background: 'rgba(168,85,247,0.22)',
                border: '1.5px solid rgba(168,85,247,0.6)'
              }}>
              
              <Sparkles size={17} color="#a855f7" />
            </div>

            {/* Count badge top-right */}
            <div
              className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
              style={{ background: 'var(--accent)' }}>
              
              {epicCount}
            </div>

            {/* Text bottom */}
            <div className="absolute bottom-3 left-3 right-3">
              <p className="text-sm font-bold text-white leading-tight">Epic Locations</p>
              <div className="flex items-center gap-0.5 mt-0.5">
                <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.75)' }}>
                  Naturtipps ansehen
                </span>
                <ChevronRight size={11} color="rgba(255,255,255,0.75)" />
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* ── WINEWOOD TV BANNER ── */}
      <div className="px-4 mb-4">
        <Link href="/tv">
          <div
            className="flex items-center gap-2.5 px-3 py-2.5 rounded-app transition-all duration-150 active:scale-[0.98]"
            style={{ background: 'var(--card)', border: '1px solid var(--border)' }}>
            
            <div
              className="w-8 h-8 rounded-app-sm flex items-center justify-center flex-shrink-0"
              style={{
                background: 'rgba(239,68,68,0.12)',
                border: '1px solid rgba(239,68,68,0.28)'
              }}>
              
              <Tv size={16} color="#ef4444" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-card-foreground">WINEWOOD TV</p>
              <p
                className="text-xs leading-tight mt-0.5 truncate"
                style={{ color: 'var(--muted-foreground)' }}>
                
                Schau dir abenteuerliche Videos zu Freizeitangeboten an
              </p>
            </div>
            <ChevronRight
              size={16}
              color="var(--muted-foreground)"
              className="flex-shrink-0" />
            
          </div>
        </Link>
      </div>

      {/* ── NEARBY CTA ── */}
      <div className="px-4 mb-6">
        <button
          onClick={handleNearby}
          disabled={locating}
          className="w-full flex items-center justify-center gap-2.5 py-4 rounded-pill text-base font-bold text-white transition-all duration-150 active:scale-[0.98] disabled:opacity-75"
          style={{
            background: nearbyActive ?
            'var(--primary)' :
            locating ?
            'var(--primary)' :
            'linear-gradient(135deg, #7CB342 0%, #9CCC65 100%)'
          }}
          aria-label="Angebote in meiner Nähe finden">
          
          {locating ?
          <Loader2 size={20} className="animate-spin" /> :

          <MapPin size={20} strokeWidth={2.2} />
          }
          {locating ?
          'Standort wird ermittelt...' :
          nearbyActive ?
          'Nach Entfernung sortiert ✓' : 'Was ist in meiner Nähe?'}
        </button>
      </div>

      {/* ── OFFER FEED ── */}
      <div className="px-4">
        {/* Section header */}
        <div className="flex items-center gap-2 mb-4">
          {nearbyActive ? (
            <MapPin size={18} color="var(--primary)" strokeWidth={2.2} />
          ) : (
            <TrendingUp size={18} color="var(--primary)" strokeWidth={2.2} />
          )}
          <h2 className="text-base font-semibold text-card-foreground">
            {nearbyActive ? 'In deiner Nähe' : activeCategory || searchValue ? 'Gefilterte Angebote' : 'Beliebte Angebote'}
          </h2>
          {(activeCategory || searchValue) &&
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-pill ml-auto"
            style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>
            
              {filteredOffers.length}
            </span>
          }
        </div>

        {/* Cards */}
        {filteredOffers.length === 0 ?
        <div className="text-center py-12">
            <div
            className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ background: 'var(--muted)' }}>
            
              <Search size={24} color="var(--muted-foreground)" />
            </div>
            <p className="text-sm font-semibold text-card-foreground">Keine Angebote gefunden</p>
            <p className="text-xs text-muted-foreground mt-1">
              Versuche eine andere Suche oder entferne den Filter.
            </p>
            <button
            onClick={() => {setActiveCategory(null);setSearchValue('');}}
            className="mt-3 text-xs font-semibold transition-colors duration-150"
            style={{ color: 'var(--primary)' }}>
            
              Filter zurücksetzen
            </button>
          </div> :

        <div className="flex flex-col gap-4">
            {filteredOffers.map((offer, offerIdx) => {
            const isFav = favoriteIds.has(offer.id);
            const isEpic = offer.is_epic_location;
            const isSpecial = offer.is_special;
            const isFree = offer.price === 0;
            const hasDiscount = offer.discount_amount > 0;
            const discountedPrice = offer.price - offer.discount_amount;
            const popularityScore = (offer.click_count ?? 0) + (offer.redemption_count ?? 0);
            const isPopular = !nearbyActive && popularityScore >= 5;

            // Inject a feed ad after every FEED_AD_EVERY offers
            const showAdAfter = feedAds.length > 0 && (offerIdx + 1) % FEED_AD_EVERY === 0;
            const adToShow = showAdAfter ? feedAds[Math.floor((offerIdx + 1) / FEED_AD_EVERY - 1) % feedAds.length] : null;

            const cardBorderColor = isEpic ?
            'rgba(168,85,247,0.5)' :
            isSpecial ?
            'rgba(124,179,66,0.45)' :
            'var(--border)';

            return (
              <React.Fragment key={`disc-offer-${offer.id}`}>
              <Link href={`/offer-detail-page?id=${offer.id}`} onClick={() => handleOfferClick(offer.id)}>
                  <div
                  className="rounded-app overflow-hidden transition-transform duration-150 active:scale-[0.98]"
                  style={{
                    background: 'var(--card)',
                    border: `1.5px solid ${cardBorderColor}`,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.35)'
                  }}>
                  
                    {/* Image */}
                    <div className="relative" style={{ height: '210px' }}>
                      <SafeImage
                      src={offer.image_url}
                      alt={`${offer.title} in ${offer.city} — Freizeitangebot Bild`}
                      fill
                      className="object-cover"
                      sizes="(max-width: 430px) 100vw, 430px" />
                    
                      {/* Gradient overlay */}
                      <div
                      className="absolute inset-0"
                      style={{
                        background:
                        'linear-gradient(to top, rgba(0,0,0,0.82) 0%, rgba(0,0,0,0.25) 45%, rgba(0,0,0,0) 100%)'
                      }} />
                    

                      {/* Fav + Share — top left */}
                      <div className="absolute top-3 left-3 flex gap-2">
                        <button
                        onClick={(e) => toggleFavorite(offer.id, e)}
                        className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
                        style={{
                          background: 'rgba(0,0,0,0.5)',
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)'
                        }}
                        aria-label={isFav ? 'Von Favoriten entfernen' : 'Zu Favoriten hinzufügen'}>
                        
                          <Heart
                          size={17}
                          color={isFav ? '#ef4444' : 'white'}
                          fill={isFav ? '#ef4444' : 'none'} />
                        
                        </button>
                        <button
                        onClick={(e) => handleShare(offer, e)}
                        className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-90"
                        style={{
                          background: 'rgba(0,0,0,0.5)',
                          backdropFilter: 'blur(8px)',
                          WebkitBackdropFilter: 'blur(8px)'
                        }}
                        aria-label={`${offer.title} teilen`}>
                        
                          <Share2 size={16} color="white" />
                        </button>
                      </div>

                      {/* Distance — top right */}
                      {offer.distance_km !== undefined &&
                    <div
                      className="absolute top-3 right-3 flex items-center gap-1 px-2.5 py-1 rounded-pill"
                      style={{
                        background: 'rgba(0,0,0,0.58)',
                        backdropFilter: 'blur(8px)',
                        WebkitBackdropFilter: 'blur(8px)'
                      }}>
                      
                          <MapPin size={11} color="var(--primary)" />
                          <span
                        className="text-[11px] font-semibold text-white"
                        style={{ fontVariantNumeric: 'tabular-nums' }}>
                        
                            {offer.distance_km < 1 ?
                        `${(offer.distance_km * 1000).toFixed(0)} m` :
                        `${offer.distance_km.toFixed(1)} km`}
                          </span>
                        </div>
                    }

                      {/* Epic / Special badge — bottom left of image */}
                      <div className="absolute bottom-3 left-3 flex gap-1.5">
                        {isEpic &&
                      <div
                        className="flex items-center gap-1 px-2.5 py-1 rounded-pill text-[11px] font-bold text-white"
                        style={{ background: 'var(--accent)' }}>
                        
                            <Sparkles size={11} />
                            Epic
                          </div>
                      }
                        {!isEpic && isSpecial &&
                      <div
                        className="flex items-center gap-1 px-2.5 py-1 rounded-pill text-[11px] font-bold text-white"
                        style={{ background: 'var(--primary)' }}>
                        
                            <Star size={11} fill="white" />
                            Spezial
                          </div>
                      }
                        {isPopular && !isEpic && !isSpecial &&
                      <div
                        className="flex items-center gap-1 px-2.5 py-1 rounded-pill text-[11px] font-bold text-white"
                        style={{ background: 'rgba(239,68,68,0.85)' }}>
                        
                            🔥 Beliebt
                          </div>
                      }
                      </div>
                    </div>

                    {/* Card body */}
                    <div className="p-3.5">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-card-foreground leading-tight">
                            {offer.title}
                          </h3>
                          <div className="flex items-center gap-1 mt-1">
                            <MapPin size={11} color="var(--muted-foreground)" />
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                              {offer.city}
                            </span>
                          </div>
                        </div>

                        {/* Price */}
                        <div className="text-right flex-shrink-0">
                          {isFree ?
                        <span
                          className="text-sm font-bold"
                          style={{ color: 'var(--success)' }}>
                          
                              Kostenlos
                            </span> :
                        hasDiscount && offer.requires_plus ?
                        <div className="flex flex-col items-end">
                              <span
                            className="text-xs line-through"
                            style={{
                              color: 'var(--muted-foreground)',
                              fontVariantNumeric: 'tabular-nums'
                            }}>
                            
                                {offer.price.toFixed(2).replace('.', ',')} €
                              </span>
                              <div className="flex items-center gap-1">
                                <Crown size={11} color="var(--gold)" />
                                <span
                              className="text-sm font-bold"
                              style={{
                                color: 'var(--gold)',
                                fontVariantNumeric: 'tabular-nums'
                              }}>
                              
                                  {discountedPrice.toFixed(2).replace('.', ',')} €
                                </span>
                              </div>
                            </div> :

                        <span
                          className="text-sm font-bold text-card-foreground"
                          style={{ fontVariantNumeric: 'tabular-nums' }}>
                          
                              {offer.price.toFixed(2).replace('.', ',')} €
                            </span>
                        }
                        </div>
                      </div>

                      {/* Discount hint */}
                      {offer.discount_text &&
                    <div className="mt-2.5 flex items-center gap-1.5">
                          <div
                        className="flex items-center gap-1 px-2.5 py-1 rounded-pill"
                        style={{
                          background: offer.requires_plus ?
                          'rgba(234,179,8,0.1)' :
                          'rgba(124,179,66,0.1)',
                          border: `1px solid ${
                          offer.requires_plus ?
                          'rgba(234,179,8,0.3)' :
                          'rgba(124,179,66,0.3)'}`

                        }}>
                        
                            {offer.requires_plus ?
                        <Crown size={11} color="var(--gold)" /> :

                        <Tag size={11} color="var(--primary)" />
                        }
                            <span
                          className="text-[11px] font-semibold"
                          style={{
                            color: offer.requires_plus ? 'var(--gold)' : 'var(--primary)'
                          }}>
                          
                              {offer.discount_text}
                            </span>
                          </div>
                          {offer.requires_plus &&
                      <span
                        className="text-[10px]"
                        style={{ color: 'var(--muted-foreground)' }}>
                        
                              nur WINEWOOD+
                            </span>
                      }
                        </div>
                    }

                      {/* Category pills */}
                      {offer.categories.length > 0 &&
                    <div className="flex gap-1.5 mt-2.5 flex-wrap">
                          {offer.categories.slice(0, 2).map((cat) =>
                      <span
                        key={`dcard-cat-${offer.id}-${cat.id}`}
                        className="text-[10px] font-medium px-2 py-0.5 rounded-pill"
                        style={{
                          background: 'var(--muted)',
                          color: 'var(--muted-foreground)'
                        }}>
                        
                              {cat.name}
                            </span>
                      )}
                        </div>
                    }

                      {/* W+ directions hint for epic locations */}
                      {offer.requires_plus_directions && isEpic &&
                    <div
                      className="flex items-center gap-1.5 mt-2.5 px-2.5 py-1.5 rounded-lg"
                      style={{
                        background: 'rgba(168,85,247,0.1)',
                        border: '1px solid rgba(168,85,247,0.3)'
                      }}>
                      
                          <Navigation size={11} color="#a855f7" />
                          <span className="text-[11px] font-semibold" style={{ color: '#a855f7' }}>
                            Genaue Wegbeschreibung mit
                          </span>
                          <Crown size={11} color="#f59e0b" />
                          <span className="text-[11px] font-bold" style={{ color: '#f59e0b' }}>W+</span>
                        </div>
                    }

                    </div>
                  </div>
                </Link>

                {/* Feed Ad injection */}
                {adToShow && (
                  <a
                    key={`feed-ad-${adToShow.id}-${offerIdx}`}
                    href={adToShow.target_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block rounded-app overflow-hidden transition-transform duration-150 active:scale-[0.98]"
                    style={{ background: 'var(--card)', border: '1px solid rgba(124,179,66,0.3)' }}
                  >
                    {adToShow.image_url && (
                      <div className="relative" style={{ height: '140px' }}>
                        <img src={adToShow.image_url} alt={adToShow.title} className="w-full h-full object-cover" />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0) 60%)' }} />
                        <div className="absolute bottom-2 left-3 right-3 flex items-end justify-between">
                          <p className="text-sm font-bold text-white leading-tight">{adToShow.title}</p>
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.6)' }}>Werbung</span>
                        </div>
                      </div>
                    )}
                    {!adToShow.image_url && (
                      <div className="px-4 py-3 flex items-center justify-between">
                        <p className="text-sm font-semibold" style={{ color: 'var(--card-foreground)' }}>{adToShow.title}</p>
                        <span className="text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: 'var(--muted)', color: 'var(--muted-foreground)' }}>Werbung</span>
                      </div>
                    )}
                  </a>
                )}
              </React.Fragment>);

          })}
          </div>
        }
      </div>

      {/* Bottom padding */}
      <div className="h-6" />

      {/* Legal footer */}
      <LegalFooter />

      {/* Inquiry modal for feed cards */}
      {inquiryOffer && (
        <InquiryModal
          isOpen={!!inquiryOffer}
          onClose={() => setInquiryOffer(null)}
          offer={inquiryOffer}
          isLoggedIn={!!user}
          userName={user?.user_metadata?.full_name || user?.user_metadata?.name || ''}
          userEmail={user?.email || ''}
        />
      )}
    </div>);

}